import { FC, useState } from 'react'

import { DomainCheck, DomainStatus, GeneratedSite, checkSiteDomain } from '@api/generated-site'
import { useReserveSiteDomain, useResendDomainVerificationEmail } from '@hooks/queries/generated-site'

type DomainPanelProps = {
  site: GeneratedSite
  onClose: () => void
}

const IN_PROGRESS: DomainStatus[] = ['RESERVING', 'REGISTERED', 'DNS_CONFIGURED', 'CERT_PENDING']

const RESERVE_ERRORS: Record<string, string> = {
  not_purchased: 'Activez d’abord votre site pour réserver un domaine.',
  taken: 'Ce domaine vient d’être pris, essayez-en un autre.',
  premium: 'Ce domaine est un domaine premium, non inclus dans l’offre.',
  tld_not_allowed: 'Cette extension n’est pas proposée pour le moment.',
  already_has_domain: 'Un domaine est déjà réservé pour votre site.',
  contact_incomplete: 'Vos coordonnées sont incomplètes pour enregistrer un domaine à votre nom : écrivez-nous depuis la rubrique Aide.',
  contact_invalid: 'Le registrar n’a pas accepté vos coordonnées : écrivez-nous depuis la rubrique Aide.',
  domain_purchase_disabled: 'La réservation de domaine n’est pas disponible pour le moment.',
}

const errorCodeOf = (error: unknown) => (error as { response?: { data?: { code?: string } } })?.response?.data?.code || ''

const suggest = (brand: string | null) => {
  const base = (brand || 'mon-entreprise').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  return `${base}.com`
}

// le registrar exige que le titulaire confirme son adresse email (ICANN) : rappel et renvoi de l'email
const IcannNotice: FC = () => {
  const resend = useResendDomainVerificationEmail()
  return (
    <div className="mt-4 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <p>
        Une dernière étape vous concerne : le registrar vous a envoyé un email pour confirmer votre adresse, comme l’exige l’ICANN.
        Cliquez sur le lien qu’il contient sous 15 jours, sans quoi le domaine serait suspendu.
      </p>
      <button
        type="button"
        onClick={() => resend.mutate()}
        disabled={resend.isPending || resend.isSuccess}
        className="mt-2 font-medium underline underline-offset-4 disabled:opacity-60"
      >
        {resend.isSuccess ? 'Email renvoyé, vérifiez votre boîte de réception.' : resend.isPending ? 'Envoi…' : 'Renvoyer l’email de vérification'}
      </button>
      {resend.isError && <p className="mt-1 text-red-700">L’envoi a échoué, réessayez dans un instant.</p>}
    </div>
  )
}

const DomainStatusView: FC<{ site: GeneratedSite }> = ({ site }) => {
  const status = site.domainStatus
  return (
    <>
      {status === 'ACTIVE' ? (
        <div className="rounded-2xl bg-green-50 px-4 py-3 text-sm text-green-800">
          Votre domaine est actif : votre site est en ligne sur{' '}
          <a href={`https://www.${site.domain}`} target="_blank" rel="noopener noreferrer" className="font-medium underline underline-offset-4 break-all">{`https://www.${site.domain}`}</a>.
        </div>
      ) : (
        <div className="rounded-2xl bg-black/[0.04] px-4 py-3 text-sm text-black/70">
          <div className="font-medium text-black">Activation en cours</div>
          Votre domaine sera actif sous une heure. En attendant, votre site reste accessible à son adresse actuelle, qui redirigera ensuite vers votre domaine.
        </div>
      )}
      {site.domainVerificationPending && <IcannNotice />}
      <div className="mt-3 text-[11px] text-black/50">
        Domaine enregistré à votre nom, renouvellement automatique inclus tant que votre site est actif.
      </div>
    </>
  )
}

// nom de domaine inclus dans l'offre : vérification en direct, réservation en un clic, puis suivi de l'activation
const DomainPanel: FC<DomainPanelProps> = ({ site, onClose }) => {
  const reserve = useReserveSiteDomain()
  const [domain, setDomain] = useState(site.domain || suggest(site.commercialBrand))
  const [check, setCheck] = useState<DomainCheck | null>(null)
  const [checking, setChecking] = useState(false)
  const [checkError, setCheckError] = useState<string | null>(null)

  const hasDomain = !!site.domain && site.domainStatus !== 'FAILED' && (site.domainStatus === 'ACTIVE' || IN_PROGRESS.includes(site.domainStatus as DomainStatus))
  const reserveError = reserve.isError ? (RESERVE_ERRORS[errorCodeOf(reserve.error)] || 'La réservation a échoué, réessayez dans quelques minutes.') : null

  const runCheck = async () => {
    setChecking(true); setCheckError(null); setCheck(null)
    try {
      const { data } = await checkSiteDomain(domain)
      setCheck(data)
    } catch (e) {
      setCheckError(errorCodeOf(e) === 'invalid_domain' ? 'Ce nom de domaine n’est pas valide (ex. mon-entreprise.com).' : 'Vérification impossible pour le moment, réessayez.')
    } finally {
      setChecking(false)
    }
  }

  const onReserve = () => {
    if (!check?.available || !check.included) return
    reserve.mutate(check.domain)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6">
      <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full md:max-w-[560px] max-h-[94vh] overflow-y-auto rounded-t-3xl md:rounded-3xl bg-white p-6 md:p-8 shadow-2xl animate-fade-up">
        <button type="button" onClick={onClose} aria-label="Fermer" className="absolute right-4 top-4 h-9 w-9 rounded-full bg-black/5 text-lg leading-none hover:bg-black/10">×</button>

        <div className="text-[11px] uppercase tracking-[0.2em] text-black/50 mb-2">Votre nom de domaine</div>
        <h2 className="font-aeonik text-2xl md:text-[28px] font-medium leading-tight mb-3">
          {hasDomain ? site.domain : 'Choisissez l’adresse de votre site.'}
        </h2>

        {hasDomain ? (
          <DomainStatusView site={site} />
        ) : (
          <>
            {site.domainStatus === 'FAILED' && (
              <div className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                La réservation de {site.domain} a échoué. Réessayez avec un autre nom ou écrivez-nous depuis la rubrique Aide.
              </div>
            )}
            <p className="text-sm text-black/60 mb-5">
              Le nom de domaine est inclus dans votre offre (hors domaines premium). Nous l’enregistrons à votre nom, le configurons et le renouvelons pour vous.
            </p>
            <div className="flex gap-2">
              <input
                id="site-domain"
                type="text"
                value={domain}
                onChange={(event) => { setDomain(event.target.value.toLowerCase()); setCheck(null) }}
                onKeyDown={(event) => { if (event.key === 'Enter') runCheck() }}
                placeholder="mon-entreprise.com"
                className="grow rounded-xl border border-black/15 bg-white text-base focus:border-black focus:outline-none [&&&]:h-12 [&&&]:px-4 [&&&]:mb-0"
              />
              <button type="button" onClick={runCheck} disabled={checking || domain.length < 4} className="rounded-xl bg-black px-5 text-white font-medium disabled:opacity-50">
                {checking ? '…' : 'Vérifier'}
              </button>
            </div>

            {check && (
              <div className={`mt-4 rounded-2xl px-4 py-3 text-sm ${check.available && check.included ? 'bg-green-50 text-green-800' : 'bg-black/[0.04] text-black/70'}`}>
                {check.available && check.included && <><strong>{check.domain}</strong> est disponible et inclus dans votre offre.</>}
                {check.available && !check.included && <><strong>{check.domain}</strong> est disponible mais c’est un domaine premium, non inclus.</>}
                {!check.available && check.reason === 'tld_not_allowed' && <>Extensions proposées : {(check.allowedTlds || []).map((t) => `.${t}`).join(', ')}.</>}
                {!check.available && check.reason === 'taken' && <><strong>{check.domain}</strong> est déjà pris. Essayez une variante (ville, activité).</>}
              </div>
            )}
            {(checkError || reserveError) && <div className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{checkError || reserveError}</div>}

            <button
              type="button"
              onClick={onReserve}
              disabled={!check?.available || !check?.included || reserve.isPending || !site.purchasedAt}
              className="mt-5 w-full rounded-2xl bg-black py-4 font-aeonik text-base font-medium text-white hover:bg-black/90 disabled:opacity-50"
            >
              {reserve.isPending ? 'Réservation…' : site.purchasedAt ? 'Réserver ce domaine (inclus)' : 'Activez votre site pour réserver un domaine'}
            </button>
            <div className="mt-3 text-center text-[11px] text-black/50">
              Enregistrement à votre nom, renouvellement automatique inclus tant que votre site est actif. Le registrar vous demandera de confirmer votre adresse email.
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default DomainPanel
